// AM Advertising Home — the page's persisted view prefs, riding the existing
// gridPreferences envelope (a new gridKey), which also means the team-default
// save model covers it for free. Pacing toggle · display currency · sort.
//
// The DATE RANGE + COMPARISON MODE + SCOPE are deliberately session state, like
// the other dashboards' filters — a saved date range is a stale question.

import type { GridPreferences } from '../../types/user';
import type { AdChannel, ComparisonMode } from '../../types/advertisingHome';
import { NATIVE_DISPLAY_CURRENCY } from '../../lib/reporting/currencyConversion';
// ⚠️ The SAME ccy: decoder the two reporting dashboards share — a third decoder
// would be a third chance to disagree about what a stored code means.
import { decodeCurrencyToken } from '../reporting/chartPrefs';
import type { BookSortKey } from '../../lib/advertisingHome/bookRows';
import {
  isPulseChannel,
  isPulseMetric,
  type PulseChannelKey,
  type PulseMetric,
} from '../../lib/advertisingHome/pulseSeries';

export const ADVERTISING_HOME_GRID_KEY = 'advertising-home';
const STATE_PRESET_ID = 'advertising-home-state';

export interface HomeViewState {
  /**
   * The budget-pacing feature (alerts + the table column). DEFAULT OFF — not
   * all accounts have budgets set, and an empty pacing column plus a silent
   * absence of pacing alerts reads better than noise until an AM opts in.
   */
  pacing: boolean;
  /** Display currency (native = per-currency, never summed). */
  displayCurrency: string;
  sortKey: BookSortKey;
  sortDir: 'asc' | 'desc';
  /**
   * The alerts panel collapsed to its severity-count header (default expanded).
   * Persisted so an "All accounts" reviewer who prefers the table first keeps
   * that layout across visits. The verdict strip stays visible either way.
   */
  alertsCollapsed: boolean;
  /** What the three pulse cards plot — ONE selection drives all three. */
  pulseMetric: PulseMetric;
  pulseChannel: PulseChannelKey;
}

export const DEFAULT_SORT_KEY: BookSortKey = 'attention';

export function defaultHomeViewState(): HomeViewState {
  return {
    pacing: false,
    displayCurrency: NATIVE_DISPLAY_CURRENCY,
    sortKey: DEFAULT_SORT_KEY,
    sortDir: 'asc',
    alertsCollapsed: false,
    // Leads is the number an AM is judged on; blended is the whole book.
    pulseMetric: 'leads',
    pulseChannel: 'blended',
  };
}

const DEFAULT_PULSE_METRIC: PulseMetric = 'leads';
const DEFAULT_PULSE_CHANNEL: PulseChannelKey = 'blended';

const SORT_KEYS: readonly BookSortKey[] = [
  'attention',
  'name',
  'metaSpend',
  'metaLeads',
  'metaCpl',
  'googleSpend',
  'googleLeads',
  'googleCpl',
  'blendedSpend',
  'blendedLeads',
  'blendedCpl',
  'cplChange',
  'pacing',
];

function isSortKey(v: string): v is BookSortKey {
  return (SORT_KEYS as readonly string[]).includes(v);
}

export function encodeHomeViewState(state: HomeViewState): GridPreferences {
  const tokens: string[] = [
    // Only non-defaults are written — absence and the default mean the same
    // thing for every token here, so no "none" markers are needed.
    ...(state.pacing ? ['pacing:1'] : []),
    ...(state.displayCurrency !== NATIVE_DISPLAY_CURRENCY
      ? [`ccy:${state.displayCurrency}`]
      : []),
    ...(state.sortKey !== DEFAULT_SORT_KEY || state.sortDir !== 'asc'
      ? [`sort:${state.sortKey}:${state.sortDir}`]
      : []),
    ...(state.alertsCollapsed ? ['alertsCollapsed:1'] : []),
    ...(state.pulseMetric !== DEFAULT_PULSE_METRIC || state.pulseChannel !== DEFAULT_PULSE_CHANNEL
      ? [`pulse:${state.pulseMetric}:${state.pulseChannel}`]
      : []),
  ];
  return {
    activePresetId: STATE_PRESET_ID,
    presets: [{ id: STATE_PRESET_ID, name: 'Advertising Home', visibleColumns: tokens }],
  };
}

export function decodeHomeViewState(prefs: GridPreferences | undefined): HomeViewState {
  const state = defaultHomeViewState();
  const preset = prefs?.presets.find((p) => p.id === STATE_PRESET_ID);
  if (!preset) return state;
  const tokens = preset.visibleColumns;

  state.pacing = tokens.includes('pacing:1');
  state.alertsCollapsed = tokens.includes('alertsCollapsed:1');
  state.displayCurrency = decodeCurrencyToken(
    tokens.find((t) => t.startsWith('ccy:'))?.slice('ccy:'.length),
  );
  const pulse = tokens.find((t) => t.startsWith('pulse:'))?.slice('pulse:'.length);
  if (pulse) {
    const [metric, channel] = pulse.split(':');
    // Garbage falls back to the defaults rather than an unexplained chart.
    if (metric && isPulseMetric(metric)) state.pulseMetric = metric;
    if (channel && isPulseChannel(channel)) state.pulseChannel = channel;
  }

  const sort = tokens.find((t) => t.startsWith('sort:'))?.slice('sort:'.length);
  if (sort) {
    const sep = sort.lastIndexOf(':');
    const key = sort.slice(0, sep);
    const dir = sort.slice(sep + 1);
    // Garbage falls back to the default rather than an unexplained order.
    if (isSortKey(key) && (dir === 'asc' || dir === 'desc')) {
      state.sortKey = key;
      state.sortDir = dir;
    }
  }
  return state;
}

/**
 * ⚠⚠ THE SPECIALIST BOARDS PERSIST UNDER THEIR OWN GRID KEYS, one per board.
 *
 * `ADVERTISING_HOME_GRID_KEY` is the AM board's envelope. Reusing it would mean
 * a Meta specialist choosing "CPL" on their own page silently rewrote the pulse
 * metric on every AM's Advertising Home — three boards, three audiences, one
 * stored value, and the AM would find their chart changed by somebody who never
 * opened their page.
 *
 * Keyed BY CHANNEL rather than two loose constants, so a third board cannot be
 * added without declaring where it stores anything.
 */
export const SPECIALIST_HOME_GRID_KEYS: Record<AdChannel, string> = {
  meta: 'advertising-meta-home',
  google: 'advertising-google-home',
};

const SPECIALIST_STATE_PRESET_ID = 'specialist-home-state';

/**
 * A specialist board's persisted view. Deliberately ONE field: the date range,
 * comparison mode and filter row are session state here exactly as they are on
 * the AM board (a saved range is a stale question), and the pulse CHANNEL is
 * fixed by which board you are on, so it is not a choice to store.
 */
export interface SpecialistViewState {
  pulseMetric: PulseMetric;
}

export function defaultSpecialistViewState(): SpecialistViewState {
  // Spend is what these boards opened on; keeping it as the default means
  // nobody's page changes until they choose something.
  return { pulseMetric: 'spend' };
}

const DEFAULT_SPECIALIST_PULSE_METRIC: PulseMetric = 'spend';

export function encodeSpecialistViewState(state: SpecialistViewState): GridPreferences {
  // Only non-defaults are written — absence and the default mean the same thing.
  const tokens =
    state.pulseMetric !== DEFAULT_SPECIALIST_PULSE_METRIC
      ? [`pulseMetric:${state.pulseMetric}`]
      : [];
  return {
    activePresetId: SPECIALIST_STATE_PRESET_ID,
    presets: [
      { id: SPECIALIST_STATE_PRESET_ID, name: 'Specialist home', visibleColumns: tokens },
    ],
  };
}

export function decodeSpecialistViewState(
  prefs: GridPreferences | undefined,
): SpecialistViewState {
  const state = defaultSpecialistViewState();
  const preset = prefs?.presets.find((p) => p.id === SPECIALIST_STATE_PRESET_ID);
  if (!preset) return state;
  const metric = preset.visibleColumns
    .find((t) => t.startsWith('pulseMetric:'))
    ?.slice('pulseMetric:'.length);
  // Garbage falls back to the default rather than an unexplained chart.
  if (metric && isPulseMetric(metric)) state.pulseMetric = metric;
  return state;
}

export const COMPARISON_MODE_OPTIONS: Array<{ value: ComparisonMode; label: string }> = [
  { value: 'previous', label: 'vs previous period' },
  { value: 'lastMonth', label: 'vs same period last month' },
  { value: 'lastYear', label: 'vs same period last year' },
];
